import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSellerBilling,
  markBillAsPaid,
  resetPaymentState,
  clearError
} from '../redux/slices/sellerBillingSlice';
import QRCode from "react-qr-code"; // Changed import
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';

const SellerBilling = () => {
  const dispatch = useDispatch();
  const { billingData, loading, error, paymentProcessing, paymentSuccess } = 
    useSelector((state) => state.billing);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [transactionId, setTransactionId] = useState('');
  const [upiId, setUpiId] = useState('7734904014@ybl'); // Default UPI ID
  const [qrValue, setQrValue] = useState(''); // For react-qr-code

  useEffect(() => {
    dispatch(fetchSellerBilling());
  }, [dispatch]);

  useEffect(() => {
    if (paymentSuccess) {
      const timer = setTimeout(() => {
        dispatch(resetPaymentState());
        setOpenPaymentDialog(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess, dispatch]);

  // Generate QR Code when dialog opens
  useEffect(() => {
    if (openPaymentDialog && billingData?.summary?.amounts?.totalBillingAmount) {
      generateQRValue();
    }
  }, [openPaymentDialog, billingData]);

  const generateQRValue = () => {
    try {
      const amount = billingData.summary.amounts.totalBillingAmount;
      // UPI QR Code format
      const upiString = `upi://pay?pa=${upiId}&pn=QuickDials&am=${amount}&cu=INR&tn=Bill%20Payment`;
      setQrValue(upiString);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handlePayNow = () => {
    setOpenPaymentDialog(true);
  };

  const handlePaymentSubmit = async () => {
    if (!transactionId && paymentMethod === 'online') {
      alert('Please enter transaction ID');
      return;
    }

    const paymentDetails = {
      method: paymentMethod,
      upiId: paymentMethod === 'upi' ? upiId : null,
      transactionId: transactionId || null,
      paidAt: new Date().toISOString()
    };

    await dispatch(markBillAsPaid({
      billId: billingData.summary.billId,
      paymentDetails
    }));
  };

  const handleRefresh = () => {
    dispatch(fetchSellerBilling());
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading && !billingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Billing Dashboard
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment successful! Your bill has been marked as paid.
        </Alert>
      )}

      {/* Main Bill Card */}
      {billingData?.summary?.hasBill ? (
        <Grid container spacing={3}>
          {/* Bill Summary Card */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold">
                  Current Month Bill
                </Typography>
                <Chip
                  label={billingData.summary.billStatus.toUpperCase()}
                  color={billingData.summary.billStatus === 'paid' ? 'success' : 'warning'}
                  icon={billingData.summary.billStatus === 'paid' ? <CheckCircleIcon /> : <ErrorIcon />}
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Billing Date
                  </Typography>
                  <Typography variant="h6">
                    {formatDate(billingData.summary.billingDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="h6" color={new Date(billingData.summary.dueDate) < new Date() ? 'error' : 'inherit'}>
                    {formatDate(billingData.summary.dueDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Buyers
                  </Typography>
                  <Typography variant="h6">
                    {billingData.summary.totalBuyers}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Verified Buyers
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {billingData.summary.verifiedBuyers}
                  </Typography>
                </Grid>
              </Grid>

              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Amount Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Base Price (Monthly)</TableCell>
                        <TableCell align="right">{formatCurrency(billingData.summary.calculation.basePrice)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Dealer Price ({billingData.summary.verifiedBuyers} buyers × ₹{billingData.summary.calculation.dealerPricePerBuyer})
                        </TableCell>
                        <TableCell align="right">{formatCurrency(billingData.summary.calculation.totalDealerPrice)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Total Amount</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            {formatCurrency(billingData.summary.amounts.totalBillingAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Payment Status */}
              <Box mt={4} p={2} bgcolor={billingData.summary.billStatus === 'paid' ? '#e8f5e8' : '#fff3e0'} borderRadius={1}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    {billingData.summary.billStatus === 'paid' ? (
                      <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                    ) : (
                      <WalletIcon color="warning" sx={{ fontSize: 40 }} />
                    )}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body1">
                      {billingData.summary.billStatus === 'paid' 
                        ? 'This bill has been paid. Thank you!' 
                        : `Pending Amount: ${formatCurrency(billingData.summary.amounts.pendingAmount)}`}
                    </Typography>
                  </Grid>
                  <Grid item>
                    {billingData.summary.billStatus !== 'paid' && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<PaymentIcon />}
                        onClick={handlePayNow}
                        disabled={paymentProcessing}
                      >
                        Pay Now
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Statistics Card */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Billing Statistics
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Total Buyers
                </Typography>
                <Typography variant="h4">{billingData.summary.totalBuyers}</Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Verified Buyers
                </Typography>
                <Typography variant="h4" color="success.main">{billingData.summary.verifiedBuyers}</Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Unverified Buyers
                </Typography>
                <Typography variant="h4" color="text.secondary">{billingData.summary.unverifiedBuyers}</Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Member Since
                </Typography>
                <Typography variant="body1">
                  {formatDate(billingData.summary.sellerSince)}
                </Typography>
              </Box>

              {billingData.unverifiedBuyers?.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Unverified Buyers
                  </Typography>
                  {billingData.unverifiedBuyers.map((buyer, index) => (
                    <Chip
                      key={index}
                      size="small"
                      icon={<InfoIcon />}
                      label={`${buyer.phone} - ${buyer.reason}`}
                      sx={{ mb: 1, mr: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Unverified Buyers List (if any) */}
          {billingData.unverifiedBuyers?.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Unverified Buyers
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These buyers won't be included in your bill until they verify their phone number.
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Business Name</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billingData.unverifiedBuyers.map((buyer, index) => (
                        <TableRow key={index}>
                          <TableCell>{buyer.phone}</TableCell>
                          <TableCell>{buyer.businessName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={buyer.reason} 
                              color="warning" 
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            No Active Bill
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your next bill will be generated on your billing date.
          </Typography>
          {billingData?.summary && (
            <Typography variant="body2" color="primary">
              Next Billing: {formatDate(billingData.summary.billingDate)}
            </Typography>
          )}
        </Paper>
      )}

      {/* Payment Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={() => !paymentProcessing && setOpenPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" />
            <Typography variant="h6">Complete Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {paymentSuccess ? (
            <Box textAlign="center" py={3}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your bill has been marked as paid.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Amount */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount to Pay
                  </Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {formatCurrency(billingData?.summary?.amounts?.totalBillingAmount || 0)}
                  </Typography>
                </Paper>
              </Grid>

              {/* QR Code - Using react-qr-code */}
              <Grid item xs={12} textAlign="center">
                <Typography variant="subtitle2" gutterBottom>
                  Scan QR Code to Pay via UPI
                </Typography>
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    display: 'inline-block',
                    borderRadius: 2,
                    boxShadow: 1
                  }}
                >
                  {qrValue ? (
                    <QRCode
                      value={qrValue}
                      size={200}
                      level="H"
                      style={{ width: '100%', maxWidth: 200, height: 'auto' }}
                    />
                  ) : (
                    <CircularProgress size={40} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  UPI ID: {upiId}
                </Typography>
              </Grid>

              {/* Payment Method Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Method
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant={paymentMethod === 'upi' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentMethod('upi')}
                    size="small"
                  >
                    UPI
                  </Button>
                  <Button
                    variant={paymentMethod === 'online' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentMethod('online')}
                    size="small"
                  >
                    Online Banking
                  </Button>
                </Box>
              </Grid>

              {/* Transaction ID (for online banking) */}
              {paymentMethod === 'online' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transaction ID"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    size="small"
                    required
                  />
                </Grid>
              )}

              {/* UPI ID (editable) */}
              {paymentMethod === 'upi' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="UPI ID"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    size="small"
                    helperText="Update if you want to change UPI ID"
                  />
                </Grid>
              )}

              {/* Instructions */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    1. Open any UPI app (Google Pay, PhonePe, Paytm)<br/>
                    2. Scan the QR code above<br/>
                    3. Complete the payment<br/>
                    4. Click "Confirm Payment" below
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {!paymentSuccess && (
            <>
              <Button onClick={() => setOpenPaymentDialog(false)} disabled={paymentProcessing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePaymentSubmit}
                disabled={paymentProcessing || (paymentMethod === 'online' && !transactionId)}
                startIcon={paymentProcessing ? <CircularProgress size={20} /> : <PaymentIcon />}
              >
                {paymentProcessing ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </>
          )}
          {paymentSuccess && (
            <Button onClick={() => setOpenPaymentDialog(false)} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SellerBilling;